import { db } from "../firebase-config";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { geminiGenerateJson } from "./geminiService";

// Module-level map to store in-flight promises to prevent duplicate AI triggers
const activeSyncPromises = new Map();

/**
 * Helper: Ensures objects are compared in a stable alphabetical order.
 * This is critical for preventing redundant AI calls when Firestore reorders keys.
 */
const sortObject = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(sortObject);
  return Object.keys(obj).sort().reduce((acc, key) => {
    acc[key] = sortObject(obj[key]);
    return acc;
  }, {});
};

export const localizationService = {
  /**
   * Fast Hydration: Fetches the existing translation document for immediate UI display.
   */
  async getStored(langKey, docId, transColl = "v2_translations") {
    const transDocId = `${langKey}_${docId}`;
    try {
      const snap = await getDoc(doc(db, transColl, transDocId));
      return snap.exists() ? snap.data() : null;
    } catch (err) {
      console.warn(`[Sync] Hydration failed for ${docId}:`, err);
      return null;
    }
  },

  /**
   * Surgical Sync: Manages the Living Registry (Master) and Translation (Delta).
   */
  async sync(langKey, docId, strings, masterColl = "v2_master_en", transColl = "v2_translations") {
    const syncId = `${langKey}_${docId}`;
    
    // 1. CONCURRENCY LOCK (Promise Hitch-hiking)
    // If a sync for this document is already running, return the existing promise
    if (activeSyncPromises.has(syncId)) {
      return activeSyncPromises.get(syncId);
    }

    const performSync = async () => {
      const masterRef = doc(db, masterColl, docId);
      const transDocId = `${langKey}_${docId}`;
      const transRef = doc(db, transColl, transDocId);

      try {
        const [masterSnap, transSnap] = await Promise.all([
          getDoc(masterRef),
          getDoc(transRef)
        ]);

        const masterData = masterSnap.exists() ? masterSnap.data() : {};
        const transData = transSnap.exists() ? transSnap.data() : {};

        // --- STAGE 1: LIVING REGISTRY (Teacher Mode) ---
        const { lastUpdated, ...storedMasterStrings } = masterData;
        
        // Merge current code strings with stored master
        const mergedMaster = sortObject({ ...storedMasterStrings, ...strings });
        
        // Stable comparison of Master English text
        const hasMasterChanged = JSON.stringify(sortObject(mergedMaster)) !== JSON.stringify(sortObject(storedMasterStrings));

        if (hasMasterChanged) {
          const newKeys = Object.keys(strings).filter(k => !storedMasterStrings[k]);
          const changedKeys = Object.keys(strings).filter(k => storedMasterStrings[k] && JSON.stringify(sortObject(strings[k])) !== JSON.stringify(sortObject(storedMasterStrings[k])));
          
          if (newKeys.length > 0) console.log(`[Sync] 📝 Master Ingested ${newKeys.length} New Keys for ${docId}:`, newKeys);
          if (changedKeys.length > 0) console.log(`[Sync] 📝 Master Updated ${changedKeys.length} Edits for ${docId}:`, changedKeys);
          
          await setDoc(masterRef, { ...mergedMaster, lastUpdated: serverTimestamp() });
        }

        if (langKey === 'en') return mergedMaster;

        // --- STAGE 2: SURGICAL DELTA DETECTION (Student Mode) ---
        const delta = {};
        Object.keys(mergedMaster).forEach(key => {
          const sourceMatchKey = `_source_${key}`;
          const existingTranslation = transData[key];
          
          // STABLE COMPARISON: Compare sorted code-obj vs sorted firestore-obj
          const masterValSerialized = JSON.stringify(sortObject(mergedMaster[key]));
          const sourceValSerialized = JSON.stringify(sortObject(transData[sourceMatchKey]));
          const translationIsStale = masterValSerialized !== sourceValSerialized;

          if (!existingTranslation || translationIsStale) {
            delta[key] = mergedMaster[key];
          }
        });

        const deltaKeys = Object.keys(delta);

        // --- STAGE 3: CHUNKED AI TRANSLATION ---
        if (deltaKeys.length > 0) {
          const CHUNK_SIZE = 30; 
          const updatedTransData = { ...transData };
          
          console.log(`[AI] 🤖 Processing ${deltaKeys.length} items for ${docId} (${langKey})...`);

          for (let i = 0; i < deltaKeys.length; i += CHUNK_SIZE) {
            const chunkKeys = deltaKeys.slice(i, i + CHUNK_SIZE);
            const chunkDelta = {};
            chunkKeys.forEach(k => chunkDelta[k] = delta[k]);

            console.log(`[AI] ⚡ Translating Chunk ${Math.floor(i/CHUNK_SIZE) + 1} (${chunkKeys.length} items)...`);
            
            const prompt = `Translate these IFIP Portal items into ${langKey}. Return ONLY a raw JSON object. CONTENT: ${JSON.stringify(chunkDelta)}`;
            const chunkResult = await geminiGenerateJson(prompt);

            Object.keys(chunkResult).forEach(key => {
              updatedTransData[key] = chunkResult[key];
              // CRITICAL: Save the source as it was provided to the AI
              updatedTransData[`_source_${key}`] = mergedMaster[key];
            });
          }

          await setDoc(transRef, { ...updatedTransData, lastUpdated: serverTimestamp() });
          console.log(`[Sync] ✅ ${docId} fully localized in ${langKey}`);
          return { ...mergedMaster, ...updatedTransData };
        }

        // --- STAGE 4: CACHE HIT ---
        console.log(`[Sync] ✅ ${docId} up-to-date in ${langKey}`);
        return { ...mergedMaster, ...transData };

      } catch (err) {
        console.error(`[Localization Error] ${docId}:`, err);
        return strings;
      } finally {
        activeSyncPromises.delete(syncId);
      }
    };

    const syncPromise = performSync();
    activeSyncPromises.set(syncId, syncPromise);
    return syncPromise;
  }
};